import React, { useState } from 'react';
import { ChevronDown, ChevronUp, UploadCloud, FileText, X, Eye, Download, Plus, File } from 'lucide-react';
import toast from 'react-hot-toast';
import { BASE_URL } from '@/lib/API';

export const SiteVisitFloorPlan = ({
  files = [],
  onFilesChange,
  existingFiles = [],
  expandedSection,
  onToggle,
  title = "01. FLOOR PLAN"
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);

  const isExpanded = typeof expandedSection !== 'undefined' 
    ? expandedSection === 'floor_plan'
    : internalExpanded;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const handleFileUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(file => {
        if (file.size > 15 * 1024 * 1024) {
          toast.error(`File ${file.name} exceeds 15MB limit.`);
          return false;
        }
        return true;
      });

      const formattedFiles = validFiles.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadId: `floorplan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      }));

      if (onFilesChange) {
        onFilesChange([...files, ...formattedFiles]);
      }
    }
    e.target.value = null;
  };

  const removeFile = (idx) => {
    if (onFilesChange) {
      onFilesChange(files.filter((_, i) => i !== idx));
    }
  };

  const totalCount = files.length + (existingFiles?.length || 0);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white mb-2 shadow-sm">
      <div 
        className="px-3 py-2.5 bg-slate-50/80 cursor-pointer flex justify-between items-center transition-colors hover:bg-slate-100/80"
        onClick={handleToggle}
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
            {title}
          </span>
          {totalCount > 0 && (
            <span className="bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
              {totalCount}
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
      </div>

      {isExpanded && (
        <div className="p-3 border-t border-slate-200 space-y-3 bg-white">
          <div className="flex items-center justify-end border-b border-slate-100 pb-2">
            {/* <span className="text-xs text-slate-500 font-medium">
              Upload architectural floor plans, layout diagrams, or PDF specifications.
            </span> */}
            <label className="flex items-center gap-1.5 text-[11px] font-bold bg-slate-900 text-white px-3 py-1.5 rounded-md hover:bg-black transition-colors shadow-sm cursor-pointer shrink-0">
              <UploadCloud size={14} /> Upload Floor Plan
              <input 
                type="file" 
                className="hidden" 
                accept=".pdf,image/*,.dwg,.dxf" 
                multiple 
                onChange={handleFileUpload} 
              />
            </label>
          </div>

          {files.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                New Floor Plans ({files.length})
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {files.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-md hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {item.preview ? (
                        <img 
                          src={item.preview} 
                          alt="preview" 
                          className="w-8 h-8 rounded object-cover border border-slate-200 shrink-0" 
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-white border border-slate-200 flex items-center justify-center shrink-0">
                          <FileText size={16} className="text-red-500" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-700 truncate">
                          {item.name}
                        </p>
                        {item.size && (
                          <p className="text-[10px] text-slate-400">
                            {(item.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-slate-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors shrink-0"
                      title="Remove file"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {existingFiles && existingFiles.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Existing Floor Plans ({existingFiles.length})
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {existingFiles.map((doc, idx) => {
                  const filename = doc.filename || doc.name || `Floor Plan ${idx + 1}`;
                  const fileUrl = doc.fileUrl ? `${BASE_URL}${doc.fileUrl}` : null;
                  return (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-md"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded bg-white border border-slate-200 flex items-center justify-center shrink-0">
                          <FileText size={16} className="text-blue-600" />
                        </div>
                        <span className="text-xs font-semibold text-slate-700 truncate">
                          {filename}
                        </span>
                      </div>
                      {fileUrl && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            import('@/lib/API').then(({ downloadFile }) => {
                              downloadFile(fileUrl, filename);
                            });
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs font-semibold flex items-center gap-1 shrink-0"
                        >
                          <Download size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {files.length === 0 && (!existingFiles || existingFiles.length === 0) && (
            <div className="text-center py-6 bg-slate-50/50 border border-dashed border-slate-200 rounded-md">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-1" />
              <p className="text-xs text-slate-500 font-medium">No floor plan documents uploaded yet.</p>
              <p className="text-[11px] text-slate-400">Click &quot;Upload Floor Plan&quot; to add layout files.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SiteVisitFloorPlan;
